import clsx from 'clsx';
import { PushPin, PushPinSlash, Play, Trash } from '@phosphor-icons/react';
import type { Scene, SceneImage } from '@/types';
import { useMediaUrl, downloadFilename } from '@/services/media';
import { MediaImage } from '@/components/MediaImage';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';

interface Props {
  scene: Scene;
  episodeUuid: string;
  onPinHero: (imageId: string | null) => void;
  onDelete: (imageId: string) => void;
}

export function ImageVariants({ scene, episodeUuid, onPinHero, onDelete }: Props) {
  const images = useEpisodeStore((s) => s.images);
  const activateScene = useSelectionStore((s) => s.activateScene);

  const variants = scene.imageIds
    .map((id) => images[id])
    .filter((x): x is SceneImage => Boolean(x) && !x.deletedAt)
    .sort((a, b) => a.createdAt - b.createdAt);

  if (variants.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-line-2 bg-surface-2 p-6 text-center text-[13px] text-text-3">
        No image variants yet. Type a prompt below to make one.
      </p>
    );
  }

  function reload(variant: SceneImage) {
    // Re-run from this variant: prefill the input with its saved state
    activateScene(scene, {
      prompt: variant.prompt,
      references: variant.references,
      fresh: variant.fresh,
    });
  }

  const heroId = scene.heroImageId ?? variants.at(-1)?.id ?? null;

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
      {variants.map((variant, idx) => {
        const isHero = variant.id === heroId;
        const isPinned = scene.heroImageId === variant.id;
        return (
          <VariantCard
            key={variant.id}
            variant={variant}
            scene={scene}
            episodeUuid={episodeUuid}
            idx={idx}
            isHero={isHero}
            isPinned={isPinned}
            onClickBody={() => reload(variant)}
            onPinToggle={() => onPinHero(isPinned ? null : variant.id)}
            onDelete={() => {
              if (confirm('Delete this variant? You can recover it within 30 days.')) {
                onDelete(variant.id);
              }
            }}
            onAnimate={() => reload(variant)}
          />
        );
      })}
    </div>
  );
}

function VariantCard({
  variant,
  scene,
  episodeUuid,
  idx,
  isHero,
  isPinned,
  onClickBody,
  onPinToggle,
  onDelete,
  onAnimate,
}: {
  variant: SceneImage;
  scene: Scene;
  episodeUuid: string;
  idx: number;
  isHero: boolean;
  isPinned: boolean;
  onClickBody: () => void;
  onPinToggle: () => void;
  onDelete: () => void;
  onAnimate: () => void;
}) {
  const downloadHref = useMediaUrl(episodeUuid, variant.path);
  return (
    <div
      className={clsx(
        'group relative aspect-[16/9] overflow-hidden rounded-md bg-surface-inset transition-all',
        isHero && 'ring-1 ring-accent',
      )}
    >
      <button
        type="button"
        onClick={onClickBody}
        className="block h-full w-full text-left"
        aria-label={`Re-run from variant ${idx + 1}`}
      >
        <MediaImage
          episodeUuid={episodeUuid}
          path={variant.path}
          loading="lazy"
          className="h-full w-full object-cover"
        />
        <span className="absolute left-2 bottom-2 rounded-sm bg-black/60 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-text-1 backdrop-blur-md">
          v{idx + 1}
        </span>
        {variant.fresh && (
          <span className="absolute right-2 bottom-2 rounded-sm bg-accent-soft px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-accent backdrop-blur-md">
            Raw
          </span>
        )}
      </button>

      <div className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <IconButton
          title={isPinned ? 'Unpin hero' : 'Pin as hero'}
          onClick={onPinToggle}
        >
          {isPinned ? <PushPinSlash size={12} /> : <PushPin size={12} />}
        </IconButton>
        {downloadHref && (
          <a
            href={downloadHref}
            download={downloadFilename(scene.episodeUuid, scene, variant, idx)}
            onClick={(e) => e.stopPropagation()}
            className="flex h-6 w-6 items-center justify-center rounded-sm bg-surface-3/80 text-text-2 transition-colors hover:bg-surface-3 hover:text-text-1"
            title="Download"
            aria-label="Download variant"
          >
            ↓
          </a>
        )}
        <IconButton title="Generate video from this image" onClick={onAnimate} accent>
          <Play size={12} weight="fill" />
        </IconButton>
        <IconButton title="Delete variant" onClick={onDelete} danger>
          <Trash size={12} />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  accent,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  accent?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      aria-label={title}
      className={clsx(
        'flex h-6 w-6 items-center justify-center rounded-sm bg-surface-3/80 transition-colors',
        accent && 'hover:bg-accent hover:text-text-on-accent',
        danger && 'hover:bg-err hover:text-text-1',
        !accent && !danger && 'text-text-2 hover:bg-surface-3 hover:text-text-1',
      )}
    >
      {children}
    </button>
  );
}
